import { useCallback, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router';
import { useInterval } from 'react-use';

import noop from 'lodash/noop';

import { DeploymentStatus, PaymentMethodStatus, TransportKind } from '@/graphql';
import useMonetaryCreditInfo from '@/hooks/useMonetaryCreditInfo';
import usePreviousPersistent from '@/hooks/usePreviousPersistent';
import { usePromiseSnackbar } from '@/hooks/usePromiseSnackbar';
import { ApolloError, InMemoryCache } from '@apollo/client';
import produce from 'immer';
import { useSnackbar } from 'notistack';

import {
  useInferenceVolumeInstantQuery,
  useMedianResponseTimeQuery,
  useWebsocketGrpcInferenceVolumeInstantQuery,
} from './Metrics/Tabs/__generated__/queries.generated';
import {
  useUpdateChainStableEnvironmentMutation,
  useUpdateModelResourcesMutation,
  useUpdateModelVersionInstanceTypeMutation,
  useUpdateOracleStableEnvironmentMutation,
  useUpdateRampUpSettingsMutation,
} from './__generated__/mutations.generated';
import {
  ChainDocument,
  ChainQueryHookResult,
  ChainStableEnvironmentsDocument,
  ChainStableEnvironmentsQueryHookResult,
  ModelDeploymentSpecDocument,
  ModelDocument,
  StableEnvironmentsDocument,
  StableEnvironmentsQueryHookResult,
  UpdateModelVersionStatusInCacheDocument,
  UpdateModelVersionStatusInCacheQuery,
  namedOperations,
  useInstancesQuery,
  useModelDeploymentSpecQuery,
  useModelVersionQuery,
} from './__generated__/queries.generated';
import { ActivateModelCheckResult, MatchParams } from './types';
import { PROD_ENV_NAME, checkIfOffline } from './utils';

const POLL_INTERVAL = 15000;

const STATUS_TRANSITIONS_TO_UPDATING_MAP: Record<DeploymentStatus, boolean> = {
  [DeploymentStatus.BuildingModel]: false,
  [DeploymentStatus.BuildingModelFailed]: false,
  [DeploymentStatus.BuildingModelStopped]: false,
  [DeploymentStatus.DeactivatedModel]: false,
  [DeploymentStatus.DeactivatingModel]: false,
  [DeploymentStatus.DeployingModel]: false,
  [DeploymentStatus.ModelDeployFailed]: true,
  [DeploymentStatus.ModelDneError]: false,
  [DeploymentStatus.ModelReady]: true,
  [DeploymentStatus.ModelUnhealthy]: true,
  [DeploymentStatus.Updating]: false,
  [DeploymentStatus.ScaledToZero]: true,
  [DeploymentStatus.ScalingFromZero]: true,
  [DeploymentStatus.ModelLoading]: true,
  [DeploymentStatus.MigratingWorkloadPlanes]: false,
};

function useNetworkInterruptionWarning(error?: ApolloError) {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (checkIfOffline(error)) {
      enqueueSnackbar("We're having trouble connecting. Please check your network connection.", {
        variant: 'default',
      });
    }
  }, [error, enqueueSnackbar]);
}

let lastHourDateRange: { start: string; end: string } | undefined;

function getLastHourDateRange() {
  const currentLastHourDateRange = {
    start: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    end: new Date().toISOString(),
  };

  // check the lastHourDateRange
  // if the current date range is close to the last one, return the last one

  if (
    lastHourDateRange &&
    Math.abs(
      new Date(lastHourDateRange.start).getTime() -
        new Date(currentLastHourDateRange.start).getTime(),
    ) < 5000
  ) {
    return lastHourDateRange;
  }

  lastHourDateRange = currentLastHourDateRange;
  return currentLastHourDateRange;
}

function useDateRange(keepSynced: boolean) {
  const [{ start, end }, setDateRange] = useState(getLastHourDateRange);

  useInterval(
    () => {
      setDateRange(getLastHourDateRange());
    },
    keepSynced ? 10000 : null,
  );

  return { start, end };
}

function getValueToShow(current?: number, previous?: number) {
  if (current === 0) return 0;

  return current || previous;
}

function useInferenceVolume(
  versionId: string,
  polling: boolean,
  skip = false,
  transportKind?: TransportKind,
) {
  const { start, end } = useDateRange(polling);
  const isWebsocket = transportKind === TransportKind.Websocket;
  const { data, loading: httpLoading } = useInferenceVolumeInstantQuery({
    variables: {
      start,
      end,
      modelVersionId: versionId,
      timeDivisorSeconds: 3600,
    },
    fetchPolicy: 'cache-first',
    skip: !versionId || skip || isWebsocket,
  });

  const { data: websocketGrpcData, loading: websocketGrpcLoading } =
    useWebsocketGrpcInferenceVolumeInstantQuery({
      variables: {
        start,
        end,
        modelVersionId: versionId,
        timeDivisorSeconds: 3600,
      },
      fetchPolicy: 'cache-first',
      skip: !versionId || skip || !isWebsocket,
    });

  // Get the appropriate data based on transport kind
  const inferenceVolume = isWebsocket
    ? websocketGrpcData?.instantWebsocketGrpcModelMetrics?.totalInferenceVolume
    : data?.instantModelMetrics?.inferenceVolume;

  const loading = isWebsocket ? websocketGrpcLoading : httpLoading;
  const prevInferenceVolume = usePreviousPersistent(inferenceVolume);
  const inferenceVolumeToShow = getValueToShow(inferenceVolume, prevInferenceVolume);

  return {
    inferenceVolume: inferenceVolumeToShow,
    loading: !(inferenceVolumeToShow || inferenceVolumeToShow === 0) && loading,
  };
}

function useMedianResponseTime(
  versionId: string,
  polling: boolean,
  skip = false,
  transportKind?: TransportKind,
) {
  const { start, end } = useDateRange(polling);

  const isWebsocket = transportKind === TransportKind.Websocket;

  const { data: httpData, loading: httpLoading } = useMedianResponseTimeQuery({
    variables: {
      start,
      end,
      modelVersionId: versionId,
      timeDivisorSeconds: 3600,
    },
    fetchPolicy: 'cache-first',
    skip: !versionId || skip || isWebsocket,
  });

  const medianResponseTime = isWebsocket
    ? null // Not shown for inline WebSocket response time metrics
    : httpData?.instantModelMetrics?.responseTimePercentiles?.p50;

  const loading = isWebsocket ? false : httpLoading; // No loading for inline WebSocket response time metrics

  const prevMedianResponseTime = usePreviousPersistent(medianResponseTime);

  const medianResponseTimeToShow = getValueToShow(medianResponseTime, prevMedianResponseTime);

  return {
    medianResponseTime: medianResponseTimeToShow,
    loading: !(medianResponseTimeToShow || medianResponseTimeToShow === 0) && loading,
  };
}

function useModelVersion(versionId: string | null) {
  const { data, loading, refetch } = useModelVersionQuery({
    variables: { id: versionId },
    fetchPolicy: 'cache-first',
    skip: !versionId,
    pollInterval: POLL_INTERVAL,
  });

  return {
    version: data?.modelVersion,
    loading,
    refetch,
  };
}

/**
 * Helper function that updates the cache to update the status of a particular model version
 * after any mutation that changes the model status
 */
function updateModelStatusInCache(
  cache: InMemoryCache,
  modelId: string,
  modelVersionId: string,
  status: DeploymentStatus,
  created?: Date,
): void {
  cache.updateQuery(
    {
      broadcast: false,
      query: UpdateModelVersionStatusInCacheDocument,
      variables: {
        id: modelId,
      },
    },
    produce((state: UpdateModelVersionStatusInCacheQuery) => {
      const version = state?.model.versions.find((v) => v.id === modelVersionId);
      const currentModelDeploymentStatus = version?.currentModelDeploymentStatus;

      if (!currentModelDeploymentStatus) {
        return;
      }
      if (created) {
        currentModelDeploymentStatus.created = created;
      } else if (currentModelDeploymentStatus.status !== status) {
        // If status changes, set `currentModelDeploymentStatus.created` to now. This may not exactly match the
        // server source of truth, but it should be close enough.
        currentModelDeploymentStatus.created = new Date().toISOString();
      }
      currentModelDeploymentStatus.status = status;
    }),
  );
}

function useActivateCheck(): ActivateModelCheckResult {
  const { monetaryCreditInfo } = useMonetaryCreditInfo();

  if (!monetaryCreditInfo) {
    // Default to being able to activate
    return ActivateModelCheckResult.CanActivate;
  }

  if (monetaryCreditInfo.paymentMethodStatus === PaymentMethodStatus.PaymentMethodFailed) {
    return ActivateModelCheckResult.PaymentMethodFailed;
  }

  if (
    monetaryCreditInfo.creditBalance === 0 &&
    monetaryCreditInfo.paymentMethodStatus === PaymentMethodStatus.NeedsPaymentMethod
  ) {
    return ActivateModelCheckResult.NeedsPaymentMethod;
  }

  return ActivateModelCheckResult.CanActivate;
}

function useInstances() {
  return useInstancesQuery({
    fetchPolicy: 'cache-first',
  });
}

function useUpdateModelVersionInstanceType() {
  const promiseSnackbar = usePromiseSnackbar();

  const [updateInstanceType, { loading }] = useUpdateModelVersionInstanceTypeMutation({
    refetchQueries: [namedOperations.Query.Model],
  });

  const action = useCallback(
    (scaleDownPreviousDeployment: boolean, deploymentId: string, instanceTypeLabel: string) =>
      promiseSnackbar(
        updateInstanceType({
          variables: {
            instanceTypeLabel,
            modelVersionId: deploymentId,
            scaleDownPreviousDeployment,
          },
        }),
        'Creating a new model deployment',
        (err) => `Model deployment's instance type could not be updated: ${err.message}`,
        true,
      ),
    [promiseSnackbar, updateInstanceType],
  );

  return useMemo(() => ({ updateInstanceType: action, loading }), [action, loading]);
}

function useUpdateModelResources(isChainlet = false) {
  const promiseSnackbar = usePromiseSnackbar();

  const [updateModelResources, { loading }] = useUpdateModelResourcesMutation({
    update(
      cache: InMemoryCache,
      {
        data: {
          updateResources: { ok, currentDeploymentSpec: updatedDeploymentSpec },
        },
      },
      { variables: { modelId } },
    ) {
      if (ok && !isChainlet) {
        const { model } = cache.readQuery({
          query: UpdateModelVersionStatusInCacheDocument,
          variables: {
            id: modelId,
          },
        });

        if (model) {
          const versionsToUpdate = model.versions
            .filter(
              (version) =>
                STATUS_TRANSITIONS_TO_UPDATING_MAP[version.currentModelDeploymentStatus.status],
            )
            .map((version) => version.id);

          for (const versionId of versionsToUpdate) {
            updateModelStatusInCache(cache, modelId, versionId, DeploymentStatus.Updating);
          }
        }
        cache.updateQuery(
          {
            query: ModelDeploymentSpecDocument,
            variables: {
              id: modelId,
            },
          },
          (data) =>
            produce(data, (draft) => {
              if (draft) {
                draft.model.currentDeploymentSpec = updatedDeploymentSpec;
              }
            }),
        );
      }
    },
  });

  const action = useCallback(
    (modelId: string, instanceTypeLabel: string) =>
      promiseSnackbar(
        updateModelResources({
          variables: {
            instanceTypeLabel,
            modelId,
          },
        }).then((response) =>
          response.data?.updateResources.ok
            ? response
            : Promise.reject<typeof response>(new Error()),
        ),
        'Updating deployment settings',
        (err) => `Model resources could not be updated: ${err.message}`,
        true,
      ),
    [promiseSnackbar, updateModelResources],
  );

  return useMemo(() => ({ updateModelResources: action, loading }), [action, loading]);
}

function useCurrentInstanceType() {
  const { modelId, chainId, tabEntityId } = useParams<MatchParams>();

  // we use this query when we are in the manage instance type for a chainlet
  const { data: modelData, loading: modelLoading } = useModelDeploymentSpecQuery({
    variables: { id: tabEntityId },
    fetchPolicy: 'cache-first',
    skip: !!modelId,
  });

  // we use this query when we are in the manage instance type for a model deployment
  const { data: modelVersionData, loading: modelVersionLoading } = useModelVersionQuery({
    variables: { id: tabEntityId },
    fetchPolicy: 'cache-first',
    skip: !!chainId,
  });

  const instanceType = chainId
    ? modelData?.model?.currentDeploymentSpec.instanceType
    : modelVersionData?.modelVersion?.instanceType;
  const loading = modelLoading || modelVersionLoading;

  return {
    instanceType,
    loading,
  };
}

function useModelDeploymentSpec() {
  const { modelId, tabEntityId: deploymentId } = useParams<MatchParams>();
  const { data, loading } = useModelDeploymentSpecQuery({
    variables: { id: modelId || deploymentId },
    fetchPolicy: 'cache-first',
  });

  return {
    model: data?.model,
    loading,
  };
}

function useUpdateEnvRampUpSettings() {
  const { modelId } = useParams<MatchParams>();
  const promiseSnackbar = usePromiseSnackbar();
  const [updateEnvRampUpSettings, { loading }] = useUpdateOracleStableEnvironmentMutation({
    update: (
      cache: InMemoryCache,
      {
        data: {
          update_oracle_stable_environment: { name },
        },
      },
      { variables: { promotionSettings } },
    ) => {
      cache.updateQuery(
        {
          query: StableEnvironmentsDocument,
          variables: {
            modelId,
          },
          broadcast: false,
        },
        produce((data: StableEnvironmentsQueryHookResult['data']) => {
          const env = data.oracleStableEnvironmentsForModel.find((env) => env.name === name);

          if (env) {
            env.rampUpWhilePromoting = promotionSettings.ramp_up_while_promoting;
            env.rampUpDurationSeconds = promotionSettings.ramp_up_duration_seconds;
            env.redeployOnPromotion = promotionSettings.redeploy_on_promotion;
          }
        }),
      );
    },
  });

  const action = useCallback(
    (
      name: string,
      rampUpWhilePromoting: boolean,
      rampUpWhilePromotingDuration: number,
      redeployOnPromotion: boolean,
      onCompleted: () => void = noop,
    ) => {
      promiseSnackbar(
        updateEnvRampUpSettings({
          variables: {
            modelId,
            name,
            promotionSettings: {
              ramp_up_while_promoting: rampUpWhilePromoting,
              ramp_up_duration_seconds: rampUpWhilePromotingDuration,
              redeploy_on_promotion: redeployOnPromotion,
            },
            autoscalingSettings: {},
          },
          onCompleted,
        }),
        'Promotion settings were updated successfully.',
        "An unexpected error occurred. Promotion settings couldn't be updated.",
      );
    },
    [promiseSnackbar, modelId, updateEnvRampUpSettings],
  );

  return useMemo(() => ({ updateEnvRampUpSettings: action, loading }), [action, loading]);
}

function useUpdateChainEnvRampUpSettings() {
  const { chainId } = useParams<MatchParams>();
  const promiseSnackbar = usePromiseSnackbar();
  const [updateChainEnvRampUpSettings, { loading }] = useUpdateChainStableEnvironmentMutation({
    update: (
      cache: InMemoryCache,
      {
        data: {
          update_chain_stable_environment: { name },
        },
      },
      { variables: { promotionSettings } },
    ) => {
      if (name === PROD_ENV_NAME) {
        cache.updateQuery(
          {
            query: ChainDocument,
            variables: {
              id: chainId,
            },
            broadcast: false,
          },
          produce((data: ChainQueryHookResult['data']) => {
            data.chain.productionPromotionSettings.rampUpWhilePromoting =
              promotionSettings.ramp_up_while_promoting;
            data.chain.productionPromotionSettings.rampUpDurationSeconds =
              promotionSettings.ramp_up_duration_seconds;
            data.chain.productionPromotionSettings.redeployOnPromotion =
              promotionSettings.redeploy_on_promotion;
          }),
        );
      }

      cache.updateQuery(
        {
          query: ChainStableEnvironmentsDocument,
          variables: {
            chainId,
          },
          broadcast: false,
        },
        produce((data: ChainStableEnvironmentsQueryHookResult['data']) => {
          const env = data.chainStableEnvironments.find((env) => env.name === name);

          if (env) {
            env.rampUpWhilePromoting = promotionSettings.ramp_up_while_promoting;
            env.rampUpDurationSeconds = promotionSettings.ramp_up_duration_seconds;
            env.redeployOnPromotion = promotionSettings.redeploy_on_promotion;
          }
        }),
      );
    },
  });

  const action = useCallback(
    (
      name: string,
      rampUpWhilePromoting: boolean,
      rampUpWhilePromotingDuration: number,
      redeployOnPromotion: boolean,
      onCompleted: () => void = noop,
    ) => {
      promiseSnackbar(
        updateChainEnvRampUpSettings({
          variables: {
            chainId,
            name,
            promotionSettings: {
              ramp_up_while_promoting: rampUpWhilePromoting,
              ramp_up_duration_seconds: rampUpWhilePromotingDuration,
              redeploy_on_promotion: redeployOnPromotion,
            },
          },
          onCompleted,
        }),
        'Promotion settings were updated successfully.',
        "An unexpected error occurred. Promotion settings couldn't be updated.",
      );
    },
    [promiseSnackbar, chainId, updateChainEnvRampUpSettings],
  );

  return useMemo(() => ({ updateChainEnvRampUpSettings: action, loading }), [action, loading]);
}

function useUpdateRampUpSettings() {
  const promiseSnackbar = usePromiseSnackbar();
  const [updateRampUpSettings, { loading }] = useUpdateRampUpSettingsMutation({
    update: (
      cache: InMemoryCache,
      {
        data: {
          update_model_settings: { model },
        },
      },
    ) => {
      cache.updateQuery(
        {
          broadcast: false,
          query: ModelDocument,
          variables: {
            id: model.id,
          },
        },
        produce((data) => {
          data.model.rampUpWhilePromoting = model.rampUpWhilePromoting;
          data.model.rampUpDurationSeconds = model.rampUpDurationSeconds;
          data.model.redeployOnPromotion = model.redeployOnPromotion;
        }),
      );
    },
  });

  const action = useCallback(
    (
      id: string,
      rampUpWhilePromoting: boolean,
      rampUpWhilePromotingDuration: number,
      redeployOnPromotion: boolean,
      onCompleted: () => void = noop,
    ) => {
      promiseSnackbar(
        updateRampUpSettings({
          variables: {
            id,
            rampUpWhilePromoting,
            rampUpWhilePromotingDuration,
            redeployOnPromotion,
          },
          onCompleted,
        }),
        'Promotion settings were updated successfully.',
        "An unexpected error occurred. Promotion settings couldn't be updated.",
      );
    },
    [promiseSnackbar, updateRampUpSettings],
  );

  return useMemo(() => ({ updateRampUpSettings: action, loading }), [action, loading]);
}

export {
  useNetworkInterruptionWarning,
  useInferenceVolume,
  useMedianResponseTime,
  useModelVersion,
  updateModelStatusInCache,
  useActivateCheck,
  POLL_INTERVAL,
  useInstances,
  useUpdateModelResources,
  useCurrentInstanceType,
  useUpdateEnvRampUpSettings,
  useUpdateRampUpSettings,
  useUpdateChainEnvRampUpSettings,
  useUpdateModelVersionInstanceType,
  useModelDeploymentSpec,
};
